import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { effect, inject, Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';

import { ENVIRONMENT } from '@env/environment';
import { DownloadReportNotificationData } from '@notifications/models/download-report-notification-data';
import { Notification } from '@notifications/models/notification';
import { NotificationStatus } from '@notifications/models/notification-status';
import { NotificationStatusResult } from '@notifications/models/notification-status-result';
import { NOTIFICATIONS_STORE } from '@notifications/stores/notifications.store';

@Injectable()
export class NotificationsService {
  readonly #httpClient = inject(HttpClient);
  readonly #notificationsStore = inject(NOTIFICATIONS_STORE);

  private notificationsProcessedIds: number[] = [];

  initialize(): void {
    effect(() => {
      const notifications = this.#notificationsStore.entities();
      const newNotifications = this.getNewNotifications(notifications);

      newNotifications.forEach(x => {
        void this.processNewNotification(x);
      });

      this.notificationsProcessedIds = [
        ...this.notificationsProcessedIds,
        ...newNotifications.map(x => x.id)
      ];
    });
  }

  //#region Private methods

  private async processNewNotification(
    notification: Notification
  ): Promise<void> {
    await this.delay(notification.initialDelay);

    while (true) {
      try {
        const result = await firstValueFrom(
          this.#httpClient.get<NotificationStatusResult>(
            `${ENVIRONMENT.qApi.endpoint}/${notification.statusUrl}`
          )
        );

        if (result.status === NotificationStatus.InProgress) {
          this.#notificationsStore.addOrUpdateNotification({
            ...notification,
            status: NotificationStatus.InProgress,
            skipToastNotification: true
          });

          continue;
        }

        this.#notificationsStore.addOrUpdateNotification({
          ...notification,
          date: new Date(),
          status: result.status,
          data: result.payLoad as DownloadReportNotificationData
        });

        break;
      } catch (e: unknown) {
        const exception = e as HttpErrorResponse;

        if (exception.status === 0 || exception.status === 408) {
          this.#notificationsStore.addOrUpdateNotification({
            ...notification,
            status: NotificationStatus.Unavailable
          });

          continue;
        }

        this.#notificationsStore.addOrUpdateNotification({
          ...notification,
          status: NotificationStatus.Failed
        });

        break;
      } finally {
        await this.delay(notification.delay);
      }
    }
  }

  private getNewNotifications(notifications: Notification[]): Notification[] {
    return notifications.filter(
      x => !this.notificationsProcessedIds.includes(x.id)
    );
  }

  private delay(seconds: number): Promise<unknown> {
    return new Promise(resolve => setTimeout(resolve, seconds * 1000));
  }

  //#endregion
}
